import { Icon, InputV2, tailwindPreset } from "@southfields-digital/mxmz-react-ui";
import { useMemo } from "react";

import styles from "./CanvasForm.module.scss";
import { formatConflictMessage, getOutputConflicts } from "./utils";

type CanvasFormProps = {
	state: (RundownCanvas | RundownTemplateCanvas)[];
	canvases: Canvas[];
	outputs?: Output[];
	canvasBackgrounds?: CanvasBackground[];
	stateUpdated?: (state: RundownCanvas[]) => void;
	disabled?: boolean;
};

const CanvasForm = ({
	state,
	canvases,
	outputs,
	canvasBackgrounds,
	stateUpdated,
	disabled = false,
}: CanvasFormProps) => {
	const backgroundOptions = useMemo(
		() => canvasBackgrounds?.map(({ name, id }) => ({ label: name, value: id })) || [],
		[canvasBackgrounds],
	);

	const outputOptions = useMemo(
		() => outputs?.map(({ id, name }) => ({ label: name, value: id })) || [],
		[outputs],
	);

	const handleSelectOutput = (canvasId: string, outputId: string) => {
		const output = outputs?.find(({ id }) => outputId === id);
		const updatedState = state.map((canvas) =>
			canvas.id !== canvasId ? canvas : { ...canvas, output, outputId: output?.id ?? null },
		);

		if (stateUpdated) {
			stateUpdated(updatedState);
		}
	};

	const handleSelectBackground = (
		canvas: RundownCanvas | RundownTemplateCanvas,
		backgroundId: CanvasBackground["id"],
	) => {
		const updatedState = state.map((c) =>
			c.id !== canvas.id ? c : { ...c, canvasBackgroundId: backgroundId ? backgroundId : null },
		);

		if (stateUpdated) {
			stateUpdated(updatedState);
		}
	};

	const conflicts = getOutputConflicts(state, canvases);

	return (
		<table className={styles.CanvasGraphicsForm}>
			<thead>
				<tr>
					<th className={styles.CanvasColumn}>Canvas</th>
					{outputs ? <th>Output</th> : null}
					<th>Background</th>
				</tr>
			</thead>
			<tbody>
				{state?.map((rundownCanvas) => {
					const inputDisabled = disabled;
					const canvas = canvases.find(({ id }) => id === rundownCanvas.id);
					const conflict = conflicts.find((c) => c.output.id === rundownCanvas.output?.id);
					return (
						<tr key={rundownCanvas.id}>
							<td className={styles.CanvasColumn}>
								<div className={styles.CanvasColumnContent}>
									<div>
										{rundownCanvas.name}
										{canvas && (
											<div className={styles.Resolution}>
												{canvas.width}×{canvas.height}
											</div>
										)}
									</div>
									{conflict && (
										<div title={formatConflictMessage(conflict)}>
											<Icon
												icon="warning"
												color={tailwindPreset.theme.extend.colors.warning}
												weight="duotone"
											/>
										</div>
									)}
								</div>
							</td>
							{outputs && (
								<td>
									<InputV2
										type="select"
										inputSize="lg"
										disabled={inputDisabled}
										onChange={(e) => handleSelectOutput(rundownCanvas.id, e.target.value)}
										options={outputOptions}
										value={rundownCanvas.output?.id ?? ""}
										placeholder="-"
										selectablePlaceholder
									/>
								</td>
							)}
							<td>
								<InputV2
									type="select"
									inputSize="lg"
									disabled={inputDisabled}
									onChange={(e) => handleSelectBackground(rundownCanvas, e.target.value)}
									options={backgroundOptions}
									value={rundownCanvas.canvasBackgroundId ?? ""}
									placeholder="-"
									selectablePlaceholder
								/>
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};

export default CanvasForm;
